import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, TextField, Button, Divider } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import CountrySelect from '../common/CountrySelect';

/**
 * Profile Form component
 * Form for editing user profile information
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data
 * @param {Function} props.handleInputChange - Function to handle input changes
 * @param {Function} props.handleCountryChange - Function to handle country select changes
 * @param {Function} props.handleSubmit - Function to handle form submission
 * @param {boolean} props.saving - Whether the form is currently saving
 * @returns {JSX.Element} The ProfileForm component
 */
const ProfileForm = ({
  formData,
  handleInputChange,
  handleCountryChange,
  handleSubmit,
  saving,
}) => {
  const { t } = useTranslation('user');

  // Ensure values are never null to prevent React warnings
  const safeFormData = Object.keys(formData).reduce((acc, key) => {
    acc[key] = formData[key] === null ? '' : formData[key];
    return acc;
  }, {});

  return (
    <form onSubmit={handleSubmit}>
      <Grid
        container
        spacing={3}
      >
        <Grid
          item
          xs={12}
        >
          <Typography
            variant="h6"
            gutterBottom
          >
            {t('forms.companyInformation')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <TextField
            label={t('forms.companyName')}
            name="companyName"
            value={safeFormData.companyName}
            onChange={handleInputChange}
            fullWidth
            required
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <TextField
            label={t('forms.taxId')}
            name="taxId"
            value={safeFormData.taxId}
            onChange={handleInputChange}
            fullWidth
            required
          />
        </Grid>
        <Grid
          item
          xs={12}
        >
          <TextField
            label={t('forms.companyAddress')}
            name="companyAddress"
            value={safeFormData.companyAddress}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
            required
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="h6"
            gutterBottom
          >
            {t('forms.contactInformation')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <TextField
            label={t('forms.emailAddress')}
            name="email"
            type="email"
            value={safeFormData.email}
            onChange={handleInputChange}
            fullWidth
            required
            disabled // Email changes might require verification
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <TextField
            label={t('forms.phoneNumber')}
            name="phoneNumber"
            value={safeFormData.phoneNumber}
            onChange={handleInputChange}
            fullWidth
            required
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="h6"
            gutterBottom
          >
            {t('forms.shippingAddress')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <TextField
            label={t('forms.shippingAddress')}
            name="shippingAddress"
            value={safeFormData.shippingAddress}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextField
            label={t('forms.city')}
            name="shippingCity"
            value={safeFormData.shippingCity}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextField
            label={t('forms.postalCode')}
            name="shippingPostalCode"
            value={safeFormData.shippingPostalCode}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <CountrySelect
            label={t('forms.country')}
            value={safeFormData.shippingCountry}
            onChange={(value) => handleCountryChange('shippingCountry', value)}
            fullWidth
          />
        </Grid>

        <Grid
          item
          xs={12}
        >
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="h6"
            gutterBottom
          >
            {t('forms.billingAddress')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <TextField
            label={t('forms.billingAddress')}
            name="billingAddress"
            value={safeFormData.billingAddress}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextField
            label={t('forms.city')}
            name="billingCity"
            value={safeFormData.billingCity}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextField
            label={t('forms.postalCode')}
            name="billingPostalCode"
            value={safeFormData.billingPostalCode}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <CountrySelect
            label={t('forms.country')}
            value={safeFormData.billingCountry}
            onChange={(value) => handleCountryChange('billingCountry', value)}
            fullWidth
          />
        </Grid>

        <Grid
          item
          xs={12}
          sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? t('forms.saving') : t('forms.saveChanges')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ProfileForm;
